import { DateValueType } from "react-tailwindcss-datepicker";

import { CustomListsAutocompleteForSequencerItem } from "../../List/components/CustomListsAutocompleteForSequencer";

/**
 * URL query params that persist the analytics controls so a page refresh (or a
 * shared link) restores the exact same report. They sit alongside
 * `analytics_filter` (the drill-down chips) — see {@link ./activeFilters}.
 */
export const ANALYTICS_LISTS_PARAM_KEY = "analytics_lists";
export const ANALYTICS_FROM_PARAM_KEY = "analytics_from";
export const ANALYTICS_TO_PARAM_KEY = "analytics_to";
export const ANALYTICS_SENDERS_PARAM_KEY = "analytics_senders";

/**
 * The comparison column persists a second, independent selection. Its params are the
 * same keys under a prefix (`analytics_b_lists`, …) so a side-by-side comparison is
 * shareable as one link, and so an un-prefixed link keeps working unchanged.
 */
export const COMPARISON_PARAM_PREFIX = "analytics_b_";

/** Applies a column prefix to an `analytics_*` param key. No prefix = the primary column. */
export const prefixedParamKey = (key: string, prefix?: string): string =>
	prefix ? key.replace(/^analytics_/, prefix) : key;

/** Serializable subset of a selected campaign. */
interface PersistedCampaign {
	campaignId: string;
	campaignName: string;
	active: boolean;
}

/** Serializable subset of a selected list — only what the controls need to re-render. */
interface PersistedSelectedList {
	id: number | string;
	title: string;
	campaigns: PersistedCampaign[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	!!value && typeof value === "object" && !Array.isArray(value);

/**
 * Normalize an unknown value into a `PersistedCampaign`, or `null` when it is
 * not a usable campaign (no string id). `active` defaults to `true` so a missing
 * flag reads as "selected" — only an explicit `false` deselects.
 */
const toPersistedCampaign = (value: unknown): PersistedCampaign | null => {
	if (!isRecord(value) || typeof value.campaignId !== "string") {
		return null;
	}

	return {
		campaignId: value.campaignId,
		campaignName: typeof value.campaignName === "string" ? value.campaignName : "",
		active: value.active !== false,
	};
};

/**
 * Normalize an unknown value into a `PersistedSelectedList`, or `null` when it
 * lacks a usable id/title. Used for both directions: it whitelists the
 * serializable fields on write (so a non-serializable `icon`/extra field never
 * leaks into the URL) and validates the shape on read.
 */
const toPersistedList = (value: unknown): PersistedSelectedList | null => {
	if (!isRecord(value)) {
		return null;
	}

	const { id, title, campaigns } = value;

	if ((typeof id !== "number" && typeof id !== "string") || typeof title !== "string") {
		return null;
	}

	return {
		id,
		title,
		campaigns: Array.isArray(campaigns)
			? campaigns.map(toPersistedCampaign).filter((c): c is PersistedCampaign => c !== null)
			: [],
	};
};

/**
 * Serialize the selected lists (and their per-campaign `active` flags) into a
 * URL-safe string, or `null` when nothing serializable is selected. Mirrors the
 * `analytics_filter` JSON approach in {@link ./activeFilters}.
 */
export const serializeSelectedLists = (
	lists: CustomListsAutocompleteForSequencerItem[]
): string | null => {
	if (!lists || lists.length === 0) {
		return null;
	}

	const persisted = lists
		.map((list) => toPersistedList({ id: list.id, title: list.title, campaigns: list.campaigns }))
		.filter((list): list is PersistedSelectedList => list !== null);

	return persisted.length > 0 ? JSON.stringify(persisted) : null;
};

/**
 * Parse the `analytics_lists` URL value back into selected-list items. Returns
 * an empty array on missing or malformed input (we never throw from a URL).
 */
export const parseSelectedLists = (
	value: string | null | undefined
): CustomListsAutocompleteForSequencerItem[] => {
	if (!value) {
		return [];
	}

	try {
		const parsed: unknown = JSON.parse(value);

		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed
			.map(toPersistedList)
			.filter((list): list is PersistedSelectedList => list !== null);
	} catch {
		return [];
	}
};

const pad2 = (value: number): string => `${value}`.padStart(2, "0");

/**
 * Format a Date (or date-like string) as a stable **local** `YYYY-MM-DD`. Using
 * local components — not `toISOString()` — avoids the UTC off-by-one-day shift
 * for users behind UTC at the picker's local-midnight value.
 */
const formatLocalDate = (value: Date | string | null | undefined): string | null => {
	if (!value) {
		return null;
	}

	const date = value instanceof Date ? value : new Date(value);

	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

/** Parse a `YYYY-MM-DD` string into a local-midnight Date, or `null` when invalid. */
const parseLocalDate = (value: string | null | undefined): Date | null => {
	if (!value) {
		return null;
	}

	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

	if (!match) {
		return null;
	}

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const date = new Date(year, month - 1, day);

	// Reject rolled-over / out-of-range values (e.g. month 13, Feb 30): JS Date
	// silently wraps them, so the constructed components must match the input.
	const inRange =
		date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

	return inRange ? date : null;
};

/** Serialize a picker range into `{ from, to }` URL strings (`null` when absent). */
export const serializeDateRange = (
	value: DateValueType
): { from: string | null; to: string | null } => ({
	from: formatLocalDate(value?.startDate ?? null),
	to: formatLocalDate(value?.endDate ?? null),
});

/** Parse the `analytics_from` / `analytics_to` URL values into a picker range. */
export const parseDateRange = (
	from: string | null | undefined,
	to: string | null | undefined
): DateValueType => ({
	startDate: parseLocalDate(from),
	endDate: parseLocalDate(to),
});

/** Set `key` to `value`, or remove it when `value` is `null`/empty. */
export const setOrDeleteParam = (
	params: URLSearchParams,
	key: string,
	value: string | null
): void => {
	if (value) {
		params.set(key, value);
	} else {
		params.delete(key);
	}
};

/** Serialize the sender selection as a comma list, or null when every sender is included. */
export const serializeSenderUserIds = (userIds: number[]): string | null =>
	userIds.length > 0 ? [...userIds].sort((a, b) => a - b).join(",") : null;

/** Parse `analytics_senders`. Never throws on a hand-edited URL — bad ids are dropped. */
export const parseSenderUserIds = (value: string | null | undefined): number[] =>
	(value ?? "")
		.split(",")
		.map((id) => Number(id.trim()))
		.filter((id) => Number.isInteger(id) && id > 0);
